/**
 * Load diagnostics — the permanent instrument for time-to-scene work.
 *
 * Three requirements shaped this:
 *
 *  1. ALWAYS COLLECTING. Optimisation work spans many sessions, and the
 *     interesting loads are the ones you didn't plan for (a bad 4G cell, a
 *     cold cache, a big scene). A tool you have to enable first is a tool you
 *     don't have when it matters. Collection is a few numbers per progress
 *     event, so it just runs.
 *  2. INVISIBLE TO CLIENTS. Nothing renders until a deliberately obscure
 *     input opens it — a key chord on desktop, a corner tap pattern on touch
 *     (phones have no keyboard, and the phone is the device under test).
 *  3. COMPARABLE ACROSS RUNS. Every report is stamped with the scene, the
 *     browser, the screen and the measured bandwidth, because "Cartoixa on
 *     Chrome iOS over 4G" versus "Cartoixa on Safari desktop over wifi" is
 *     only a meaningful comparison if the conditions travel with the numbers.
 *
 * The central measurement is CLICK → SCENE, and the honest denominator for it
 * is bandwidth: 8MB at 2 Mbps cannot beat 32s no matter how good the code is.
 * So the report pairs the timeline with throughput measured only while
 * requests were actually in flight, plus the idle share — time we spent NOT
 * downloading, which is the part that is ours to fix.
 */

/**
 * Best-effort environment fingerprint. Deliberately reports what it CANNOT
 * know rather than guessing: an exact model ("iPhone XS") is not exposed to
 * web pages by Apple, so we name the hardware class from the screen geometry
 * and say so.
 */
function fingerprint(): { device: string; browser: string; network: string } {
    const ua = navigator.userAgent;
    const nav = navigator as Navigator & { maxTouchPoints?: number };

    let device: string;
    const w = Math.min(window.screen.width, window.screen.height);
    const h = Math.max(window.screen.width, window.screen.height);
    const dpr = window.devicePixelRatio;
    if (/iPhone/.test(ua)) {
        // Apple never ships the model to the browser. Screen geometry narrows
        // it to a hardware generation, which is what actually matters for GPU.
        const cls = (w === 375 && h === 812) ? 'iPhone X/XS/11 Pro class'
            : (w === 390 && h === 844) ? 'iPhone 12-16 (6.1") class'
            : (w === 414 && h === 896) ? 'iPhone XR/11/XS Max class'
            : (w === 393 && h === 852) ? 'iPhone 14/15/16 Pro class'
            : `iPhone (${w}x${h})`;
        device = `${cls} @${dpr}`;
    } else if (/iPad/.test(ua) || (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1)) {
        device = `iPad (${w}x${h} @${dpr})`;
    } else if (/Android/.test(ua)) {
        const m = /;\s*([^;)]+)\s+Build\//.exec(ua) ?? /Android[^;]*;\s*([^;)]+)\)/.exec(ua);
        device = `Android ${m ? m[1].trim() : ''} (${w}x${h} @${dpr})`;
    } else if (/Macintosh/.test(ua)) {
        device = `Mac desktop (${window.screen.width}x${window.screen.height} @${dpr})`;
    } else if (/Windows/.test(ua)) {
        device = `Windows desktop (${window.screen.width}x${window.screen.height} @${dpr})`;
    } else {
        device = `${navigator.platform || 'unknown'} (${window.screen.width}x${window.screen.height} @${dpr})`;
    }

    // Order matters: every iOS browser carries "Safari" in its UA, and Chrome
    // carries "Safari" too, so the specific tokens must be tested first.
    const browser = /CriOS/.test(ua) ? 'Chrome (iOS)'
        : /FxiOS/.test(ua) ? 'Firefox (iOS)'
        : /EdgiOS/.test(ua) ? 'Edge (iOS)'
        : /Edg\//.test(ua) ? 'Edge'
        : /OPR\//.test(ua) ? 'Opera'
        : /Firefox\//.test(ua) ? 'Firefox'
        : /Chrome\//.test(ua) ? 'Chrome'
        : /Safari\//.test(ua) ? 'Safari'
        : 'unknown';

    // Network Information API: Chrome/Android only. Safari and every iOS
    // browser refuse it outright (fingerprinting), so on the primary test
    // device this is simply unavailable and the measured bitrate is the only
    // truth we get.
    const c = (navigator as Navigator & {
        connection?: { effectiveType?: string; downlink?: number; type?: string; saveData?: boolean };
    }).connection;
    const network = c
        ? [c.type, c.effectiveType, c.downlink ? `~${c.downlink} Mbps claimed` : null,
            c.saveData ? 'data-saver ON' : null].filter(Boolean).join(' / ')
        : 'not exposed by this browser (Safari/iOS never does) — see measured bitrate';

    return { device, browser, network };
}

const HISTORY_KEY = 'ollo:loadlog';
const HISTORY_MAX = 30;

/** True only when the URL carries `?debug=1` — the same switch that opens the
 *  diagnostics overlay in mount.ts. Read at call time rather than module load so
 *  there is no ordering dependency on mount. */
function debugRequested(): boolean {
    try {
        return new URLSearchParams(window.location.search).get('debug') === '1';
    } catch { return false; }
}

/** Append a finished report to this device's rolling log, so historical
 *  comparisons survive across sessions and reboots.
 *
 *  OFF unless `?debug=1`. This writes to the visitor's own localStorage, and the
 *  privacy policy (§3.6) states the viewer stores nothing there but a cosmetic
 *  preference. Gating it keeps that true for ordinary visitors — including on
 *  client sites — without a consent prompt. Anonymous load telemetry still goes
 *  to /v/load-log on every open; that stores nothing on the device. */
function pushHistory(entry: Record<string, unknown>): void {
    if (!debugRequested()) return;
    try {
        const raw = window.localStorage.getItem(HISTORY_KEY);
        const list: unknown[] = raw ? JSON.parse(raw) : [];
        list.push(entry);
        while (list.length > HISTORY_MAX) list.shift();
        window.localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
    } catch { /* private mode / quota — the live panel still works */ }
}

export function readHistory(): unknown[] {
    try {
        const raw = window.localStorage.getItem(HISTORY_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
}

export interface DiagProgress {
    loadedBytes: number;
    inflight: number;
    tiers: Map<number, { done: number; inflight: number }>;
}

/** Cumulative-bytes sample, for the bitrate-over-time trace. */
interface Sample { t: number; bytes: number; busy: boolean }

const MARK_ORDER = [
    'click', 'document', 'bundle', 'scene.json', 'lod-meta',
    'first chunk', 'REVEAL', 'full detail'
] as const;

const fmtS = (ms: number | null | undefined) =>
    (ms === null || ms === undefined ? '—' : `${(ms / 1000).toFixed(2)}s`);
const fmtMB = (b: number) => `${(b / 1_048_576).toFixed(2)} MB`;
const mbps = (bytes: number, ms: number) => (ms > 200 ? (bytes * 8) / 1e6 / (ms / 1000) : 0);

export class LoadDiagnostics {

    /** ms since `origin` for each milestone. */
    private marks = new Map<string, number>();

    private samples: Sample[] = [];

    private loadedBytes = 0;

    private inflight = 0;

    private tiers = new Map<number, { done: number; inflight: number }>();

    /** Accumulated time with >=1 scene request outstanding. */
    private busyMs = 0;

    private lastSampleAt = 0;

    private panel: HTMLDivElement | null = null;

    private detach: Array<() => void> = [];

    /**
     * @param origin  Time base. Ideally the moment the USER CLICKED, handed in
     *   from the host page; otherwise this document's navigation start, which
     *   misses only the parent's click→iframe hop.
     */
    /** Human scene name ("cartoixa"), set once the scene.json is parsed. */
    private sceneName: string | null = null;

    private env = fingerprint();

    private historySaved = false;

    private beaconSent = false;

    constructor(
        private parent: HTMLElement,
        private sceneUrl: string,
        private origin: number,
        clickWasReal: boolean,
        /** false = never report this load (stand demo: our own devices). */
        private beacon: boolean = true
    ) {
        this.marks.set('document', 0);
        if (clickWasReal) this.marks.set('click', 0);
        this.mark('bundle');
        this.lastSampleAt = this.now();
        this.installOpeners();
        // A load the visitor walked away from still tells us something — in
        // fact it tells us the most. pagehide fires on iOS where unload does not.
        // Also the flush for loads that revealed but never reached 'full
        // detail' before the visitor left — the beacon waits for full detail
        // so that number is in it, so leaving early must send what we have.
        const onLeave = () => { this.sendBeacon(!this.marks.has('REVEAL')); };
        window.addEventListener('pagehide', onLeave);
        this.detach.push(() => window.removeEventListener('pagehide', onLeave));
    }

    private now(): number {
        return performance.now() + (performance.timeOrigin - this.origin);
    }

    mark(name: string): void {
        if (!this.marks.has(name)) this.marks.set(name, this.now());
        // Persist once the run has a headline number worth comparing later.
        if (name === 'REVEAL' && !this.historySaved) {
            this.historySaved = true;
            pushHistory({
                at: new Date().toISOString(),
                scene: this.sceneLabel(),
                clickToSceneMs: Math.round(this.marks.get('REVEAL') ?? 0),
                bytes: this.loadedBytes,
                mbpsAvg: +mbps(this.loadedBytes, this.busyMs).toFixed(1),
                idlePct: this.idlePct(),
                device: this.env.device,
                browser: this.env.browser,
                network: this.env.network
            });
        }
        // Ship on FULL DETAIL, not on reveal: the beacon is sent once, and at
        // reveal the settle mark has not landed yet — full_detail_ms was 0 on
        // every row in prod. waitForSplatSettled always resolves (wedge /
        // absolute-cap paths included), so this mark always arrives; pagehide
        // covers the visitor leaving before it does.
        if (name === 'full detail') this.sendBeacon(false);
    }

    /**
     * Ship this load to the backend. Fired on reveal, and again from pagehide
     * for loads that never revealed — an abandoned load is the most
     * informative row we get, and it is precisely the one a manual test never
     * captures.
     *
     * sendBeacon, not fetch: it survives the page being torn down, and it
     * cannot delay navigation. The endpoint lives on whatever origin served
     * the scene, so there is nothing to configure and a local/relative scene
     * simply reports nothing.
     */
    private sendBeacon(abandoned: boolean): void {
        if (this.beaconSent || !this.beacon) return;
        let endpoint: string;
        try {
            const u = new URL(this.sceneUrl, window.location.href);
            if (u.origin === window.location.origin) return; // local dev scene
            endpoint = `${u.origin}/v/load-log`;
        } catch { return; }
        this.beaconSent = true;
        const payload = {
            scene: this.sceneLabel(),
            clickToSceneMs: this.marks.has('REVEAL') ? Math.round(this.marks.get('REVEAL')!) : null,
            fullDetailMs: this.marks.has('full detail') ? Math.round(this.marks.get('full detail')!) : null,
            bytes: this.loadedBytes,
            mbpsAvg: +mbps(this.loadedBytes, this.busyMs).toFixed(2),
            idlePct: this.idlePct(),
            device: this.env.device,
            browser: this.env.browser,
            network: this.env.network,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            revealReason: JSON.stringify(
                (window as unknown as Record<string, unknown>).__revealReason ?? null
            ).slice(0, 128),
            abandoned,
            embedded: window.parent !== window
        };
        try {
            // text/plain deliberately: it keeps this a CORS "simple request",
            // so there is no preflight to fail. An application/json beacon is
            // preflighted, and the viewer CORS allowlist cannot contain every
            // client site that embeds us — the preflight was silently dropping
            // every row. The endpoint reads the body as text and parses it.
            navigator.sendBeacon(endpoint, new Blob([JSON.stringify(payload)], { type: 'text/plain' }));
        } catch { /* telemetry must never break the viewer */ }
    }

    setScene(name: string): void {
        if (name) this.sceneName = name;
    }

    private sceneLabel(): string {
        if (this.sceneName) return this.sceneName;
        try {
            const parts = new URL(this.sceneUrl, window.location.href).pathname.split('/').filter(Boolean);
            return parts[parts.length - 2] ?? this.sceneUrl;
        } catch { return this.sceneUrl; }
    }

    private idlePct(): number {
        const e = this.now();
        return e > 0 ? Math.round((Math.max(0, e - this.busyMs) / e) * 100) : 0;
    }

    /** Fed from mount's byte tracker on every progress event. */
    onProgress(p: DiagProgress): void {
        const t = this.now();
        if (this.inflight > 0) this.busyMs += t - this.lastSampleAt;
        this.lastSampleAt = t;
        this.loadedBytes = p.loadedBytes;
        this.inflight = p.inflight;
        this.tiers = p.tiers;
        // One sample per ~500ms is plenty for a bitrate trace and keeps this
        // bounded on a multi-minute load.
        const last = this.samples[this.samples.length - 1];
        if (!last || t - last.t >= 500) this.samples.push({ t, bytes: p.loadedBytes, busy: p.inflight > 0 });
        if (p.loadedBytes > 0) this.mark('scene.json');
        if ([...p.tiers.values()].some(v => v.done > 0)) this.mark('first chunk');
    }

    /** Bitrate per interval, so a stall or a ramp is visible rather than averaged away. */
    private bitrateTrace(): string {
        const rows: string[] = [];
        for (let i = 1; i < this.samples.length; i++) {
            const a = this.samples[i - 1];
            const b = this.samples[i];
            const r = mbps(b.bytes - a.bytes, b.t - a.t);
            rows.push(`${(b.t / 1000).toFixed(1)}s ${r.toFixed(1).padStart(5)} Mbps${b.busy ? '' : '  (idle)'}`);
        }
        return rows.length ? rows.join('\n') : '  (no samples)';
    }

    report(): string {
        const elapsed = this.now();
        const avg = mbps(this.loadedBytes, this.busyMs);
        const idleMs = Math.max(0, elapsed - this.busyMs);
        const idlePct = elapsed > 0 ? Math.round((idleMs / elapsed) * 100) : 0;
        const reveal = this.marks.get('REVEAL') ?? null;

        const peak = this.samples.reduce((m, s, i) => {
            if (i === 0) return m;
            const a = this.samples[i - 1];
            return Math.max(m, mbps(s.bytes - a.bytes, s.t - a.t));
        }, 0);

        let verdict: string;
        if (avg === 0) verdict = 'no data yet';
        else if (idlePct >= 35) verdict = `OURS — ${idlePct}% of elapsed time had NOTHING downloading`;
        else if (avg < 3) verdict = `NETWORK-BOUND — only ${avg.toFixed(1)} Mbps available`;
        else verdict = `network ok (${avg.toFixed(1)} Mbps avg), ${idlePct}% idle`;

        // What a given bandwidth makes possible at all — the floor no code beats.
        const floor = avg > 0 ? (this.loadedBytes * 8) / 1e6 / avg : 0;

        const scene = this.sceneLabel();

        const tierRows = [...this.tiers.entries()].sort((a, b) => b[0] - a[0])
            .map(([t, v]) => `  tier ${t}: ${v.done} chunks done, ${v.inflight} pending`)
            .join('\n') || '  (none yet)';

        const timeline = MARK_ORDER
            .filter(m => this.marks.has(m))
            .map(m => `  ${m.padEnd(12)} ${fmtS(this.marks.get(m))}`)
            .join('\n');

        return [
            '── OLLOPORT LOAD REPORT ─────────────────',
            `scene        ${scene}`,
            `where        ${this.env.device} / ${this.env.browser}`,
            `CLICK→SCENE  ${fmtS(reveal)}${this.marks.has('click') ? '' : '   (from page load; parent click not reported)'}`,
            `full detail  ${fmtS(this.marks.get('full detail'))}`,
            '',
            `downloaded   ${fmtMB(this.loadedBytes)}`,
            `bandwidth    ${avg ? `${avg.toFixed(1)} Mbps avg, ${peak.toFixed(1)} peak` : '—'}`,
            `busy / idle  ${fmtS(this.busyMs)} / ${fmtS(idleMs)}  (${idlePct}% idle)`,
            `floor at avg ${floor ? `${floor.toFixed(1)}s for these bytes` : '—'}`,
            `in flight    ${this.inflight}`,
            '',
            'TIMELINE',
            timeline,
            '',
            'CHUNKS',
            tierRows,
            '',
            'BITRATE',
            this.bitrateTrace(),
            '',
            `→ ${verdict}`,
            '',
            `device       ${this.env.device}`,
            `browser      ${this.env.browser}`,
            `network      ${this.env.network}`,
            `viewport     ${window.innerWidth}x${window.innerHeight}`,
            `when         ${new Date().toISOString()}`,
            `reveal via   ${JSON.stringify((window as unknown as Record<string, unknown>).__revealReason ?? null)}`,
            `history      ${readHistory().length} previous loads on this device (HISTORY button)`,
            '─────────────────────────────────────────'
        ].join('\n');
    }

    // ── opening it ─────────────────────────────────────────────────────────
    private installOpeners(): void {
        // Shift+I. Shift exists and is labelled the same on every keyboard, so
        // no per-OS instructions; and unlike a bare typed word it cannot be
        // triggered by someone typing prose (or a URL) into the page.
        const onKey = (e: KeyboardEvent) => {
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.shiftKey && e.code === 'KeyI') { e.preventDefault(); this.toggle(); }
        };
        window.addEventListener('keydown', onKey);
        this.detach.push(() => window.removeEventListener('keydown', onKey));

        // DESKTOP ONLY, deliberately. The touch opener (five corner taps) is
        // gone: every load now reports itself to the backend automatically, so
        // there is nothing on a phone that reading the panel there unlocks —
        // and a gesture a client could stumble into is pure downside once it
        // buys us nothing.
    }

    toggle(): void {
        if (this.panel) { this.panel.remove(); this.panel = null; return; }
        const wrap = document.createElement('div');
        wrap.className = 'ollo-diag';
        const pre = document.createElement('pre');
        pre.className = 'ollo-diag-body';
        const bar = document.createElement('div');
        bar.className = 'ollo-diag-bar';

        const btn = (label: string, fn: () => void) => {
            const b = document.createElement('button');
            b.className = 'ollo-btn ollo-diag-btn';
            b.textContent = label;
            b.addEventListener('click', fn);
            bar.appendChild(b);
            return b;
        };
        const copyBtn = btn('COPY', async () => {
            const text = this.report();
            try {
                await navigator.clipboard.writeText(text);
                copyBtn.textContent = 'COPIED';
            } catch {
                // Cross-origin iframe without clipboard-write, or an old
                // WebKit: select the text so a long-press ▸ Copy works.
                const range = document.createRange();
                range.selectNodeContents(pre);
                const sel = window.getSelection();
                sel?.removeAllRanges();
                sel?.addRange(range);
                copyBtn.textContent = 'SELECTED — hold to copy';
            }
            window.setTimeout(() => { copyBtn.textContent = 'COPY'; }, 2000);
        });
        btn('SAVE JSON', async () => {
            const list = readHistory();
            const name = `ollo-loadlog-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
            const blob = new Blob([JSON.stringify(list, null, 2)], { type: 'application/json' });
            const file = new File([blob], name, { type: 'application/json' });
            const nav = navigator as Navigator & {
                canShare?: (d: { files: File[] }) => boolean;
                share?: (d: { files: File[] }) => Promise<void>;
            };
            // Phone first: an <a download> pointing at a blob is ignored by iOS
            // Safari (learned the hard way on the screenshot work), whereas the
            // share sheet can put the file into Files, Mail or AirDrop.
            if (typeof nav.canShare === 'function' && nav.canShare({ files: [file] })) {
                try { await nav.share({ files: [file] }); return; } catch { /* fall through */ }
            }
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
        });
        const histBtn = btn('HISTORY', async () => {
            const list = readHistory();
            const text = list.length
                ? list.map(e => JSON.stringify(e)).join('\n')
                : '(no previous loads recorded on this device yet)';
            pre.textContent = `── ${list.length} PREVIOUS LOADS (this device) ──\n\n${text}`;
            histBtn.textContent = 'LIVE';
            histBtn.onclick = () => { histBtn.textContent = 'HISTORY'; this.toggle(); this.toggle(); };
        });
        btn('CLOSE', () => this.toggle());

        wrap.appendChild(bar);
        wrap.appendChild(pre);
        this.parent.appendChild(wrap);
        this.panel = wrap;

        const tick = () => {
            if (!this.panel) return;
            pre.textContent = this.report();
            window.setTimeout(tick, 500);
        };
        tick();
    }

    dispose(): void {
        this.detach.forEach(fn => fn());
        this.detach = [];
        this.panel?.remove();
        this.panel = null;
    }
}
